import {
  MarketingLayout, Eyebrow, Display, SectionTitle, FocalSentence, Body,
  PrimaryCTA, SecondaryLink, Section, Hairline,
} from "./MarketingLayout";

const INCLUDED = [
  "Every room, unlocked from the first day",
  "Unlimited family members in one household",
  "Recipes, documents, appointments and bills — no limits",
  "Weekly report and weekly reset",
  "Ara's daily brief, quietly held",
  "Warm private storage for the family scrapbook",
];

const PROMISES = [
  { title: "One household, one subscription.",   body: "However many people live under one roof, you pay once for the whole family." },
  { title: "No per-seat surprises.",              body: "No feature limits designed to frustrate you into upgrading." },
  { title: "Never advertised to.",                body: "We never sell your data, advertise inside the Residence, or train third-party AI on family life." },
  { title: "Cancel any time.",                    body: "From inside your Residence, in one click. Your data leaves with you if you go." },
];

export default function PricingPage() {
  return (
    <MarketingLayout testid="pricing-page">
      {/* ── Hero ────────────────────────────────────────────────── */}
      <Section testid="pricing-hero" className="pt-24 sm:pt-32 pb-16">
        <div className="max-w-[720px]">
          <Eyebrow>Pricing</Eyebrow>
          <Display className="mt-6">
            One quiet plan for the whole household.
          </Display>
          <FocalSentence className="mt-8 max-w-[46ch]">
            Founding Members join free during beta.
          </FocalSentence>
          <Body className="mt-6 max-w-[54ch]">
            When ARA opens publicly later this year, the Residence is <strong style={{ color: 'var(--ara-ink)' }}>$99 a year</strong> — or $14 a month if you prefer. Every household, one price. No per-seat charges, ever.
          </Body>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
            <PrimaryCTA to="/join-beta" testid="pricing-hero-cta">Join the waitlist →</PrimaryCTA>
            <SecondaryLink to="/how-it-works">See how it works</SecondaryLink>
          </div>
          <p className="mt-6 text-[12.5px]" style={{ color: 'var(--ara-ink-soft)' }}>
            Founding Members lock in early pricing for life. No card required to join the waitlist.
          </p>
        </div>
      </Section>

      <Hairline />

      {/* ── What's included ─────────────────────────────────────── */}
      <Section testid="pricing-included">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-x-16 gap-y-8">
          <div>
            <Eyebrow>What’s included</Eyebrow>
          </div>
          <div className="max-w-[62ch]">
            <SectionTitle>Every room. Every family. One price.</SectionTitle>
            <ul className="mt-10 space-y-4">
              {INCLUDED.map(line => (
                <li key={line} className="flex items-baseline gap-4 text-[15.5px] leading-[1.55]" style={{ color: 'var(--ara-ink-muted)' }}>
                  <span aria-hidden style={{ color: 'var(--ara-ink-soft)' }}>·</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Hairline />

      {/* ── Promises ────────────────────────────────────────────── */}
      <Section testid="pricing-promises">
        <Eyebrow>How we think about pricing</Eyebrow>
        <SectionTitle className="mt-5 max-w-[26ch]">
          Fair, transparent, honest.
        </SectionTitle>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
          {PROMISES.map(p => (
            <div key={p.title}>
              <p
                className="text-[19px] leading-[1.25]"
                style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400 }}
              >
                {p.title}
              </p>
              <Body className="mt-3 max-w-[42ch]">{p.body}</Body>
            </div>
          ))}
        </div>
      </Section>

      <Hairline />

      {/* ── Final CTA ───────────────────────────────────────────── */}
      <Section testid="pricing-final-cta" className="pb-32">
        <div
          className="rounded-[24px] px-8 sm:px-14 py-16 sm:py-20 text-center"
          style={{ border: '1px solid var(--ara-line, rgba(31,31,31,0.08))' }}
        >
          <Eyebrow>Start your Residence</Eyebrow>
          <div className="mt-6">
            <SectionTitle className="max-w-[22ch] mx-auto">
              Join the founding households.
            </SectionTitle>
          </div>
          <Body className="mt-6 max-w-[46ch] mx-auto">
            Early access is released in small groups. Founding Members shape the Residence as it grows and hold their price for life.
          </Body>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            <PrimaryCTA to="/join-beta" testid="pricing-final-cta-btn">Join the waitlist →</PrimaryCTA>
            <SecondaryLink to="/contact">Ask a question</SecondaryLink>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
